import React, { useEffect, useState } from 'react';
import client from '../../../graphql/client';
import { ScrollView } from 'react-native';
import { Formik } from 'formik';
import { useAuthState } from '../../auth/authContext';
import Monitor, { FingerPrint } from '../../lib/monitoring/Monitor';
import UserError, { UserErrorCodes } from '../../components/styled/UserError';
import LinearGradientButton from '../../components/styled/LinearGradientButton';
import Modal from '../../components/styled/Modal';
import Text from '../../components/styled/Text';
import TextInput from '../../components/styled/TextInput';
import View from '../../components/styled/View';
import SelectedGenresWidget from '../../components/styled/SelectedGenresWidget';
import styles from './styles';
import { artistProfileSchema } from './validationSchema';
import { GENRE_TEXT, GENRE_SUB_TEXT, PROFILE_SAVED, TITLE } from './constants';

export default function ArtistProfileScreen({ navigation, route }) {
  const { cognitoId, email } = useAuthState();
  const [artist, setArtistInfo] = useState<any>();
  const [artistError, setArtistError] = useState<keyof typeof UserErrorCodes>();
  const [artistGenres, setArtistGenres] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);

  const getArtistInfo = async () => {
    const artistInfo = await client.getArtistData(cognitoId);
    if (!artistInfo) {
      const monitorData = {
        context: { user_id: cognitoId, user_type: 'artist', user_email: email },
        name: 'ArtistProfile',
        fingerprint: FingerPrint.artistAccount,
        message: 'Artist profile error',
      };
      const error =
        'Error in artistProfile page, there is no artist data for the user';
      Monitor.notify(error, monitorData);
      setArtistError(UserErrorCodes.ARTIST_PROFILE_ERROR);
      //setIsLoading(false);
    } else {
      setArtistInfo(artistInfo);
      const artistPrefGenres = artistInfo.preferredGenres.items.map(
        (artistGenre) => {
          return {
            id: artistGenre.genre.id,
            genre: artistGenre.genre.genre,
            agId: artistGenre.id,
          };
        },
      );
      setArtistGenres(artistPrefGenres);
    }
  };

  const navigateToGenresPage = () => {
    navigation.navigate('ArtistGenres', {
      artistGenres,
      isCreate: false,
    });
  };

  const updateArtistProfile = async (values) => {
    const { artistName, bio, hometown, preferredGenres } = values;

    const artistUpdateDetails = {
      cognitoId,
      artistName,
      hometown,
      biography: bio,
    };
    const updatedArtist = await client.updateArtistData(artistUpdateDetails);

    setArtistInfo(updatedArtist);
    setModalVisible(true);
  };

  useEffect(() => {
    getArtistInfo();
  }, [route?.params?.newGenres]);

  if (artistError) {
    return (
      <ScrollView contentContainerStyle={styles.errorContainer}>
        <UserError errorType={artistError} navigation={navigation} />
      </ScrollView>
    );
  }

  if (!artist || !artistGenres) return null;

  return (
    <ScrollView contentContainerStyle={styles.scrollViewContainer}>
      <Modal
        messageText={PROFILE_SAVED}
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
      />
      <View style={styles.container}>
        <Text style={styles.title}> {TITLE}</Text>
        <View
          style={styles.separator}
          lightColor="#eee"
          darkColor="rgba(255,255,255,0.1)"
        />
        <Formik
          initialValues={{
            artistName: artist.artistName,
            hometown: artist.hometown,
            bio: artist.biography,
          }}
          onSubmit={(values) => updateArtistProfile(values)}
          validationSchema={artistProfileSchema}
        >
          {({
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            touched,
            values,
          }) => (
            <>
              <View style={styles.bodyContainer}>
                <TextInput
                  autoCapitalize="none"
                  autoCompleteType="off"
                  errorMessage={touched.artistName && errors.artistName}
                  keyboardType="default"
                  label="Artist Name"
                  onBlur={handleBlur('artistName')}
                  onChangeText={handleChange('artistName')}
                  placeholder={artist.artistName}
                  value={values.artistName}
                />
                <TextInput
                  autoCompleteType="off"
                  autoCapitalize="none"
                  autoCorrect
                  errorMessage={touched.hometown && errors.hometown}
                  keyboardType="default"
                  label="Hometown"
                  onBlur={handleBlur('hometown')}
                  onChangeText={handleChange('hometown')}
                  placeholder={artist.hometown}
                  value={values.hometown}
                />
                <SelectedGenresWidget
                  editButtonOnPress={navigateToGenresPage}
                  genreButtonOnPress={() => {}}
                  genres={artistGenres}
                  isDefaultGenreButtonActive={true}
                  text={GENRE_TEXT}
                  subText={GENRE_SUB_TEXT}
                />
                <TextInput
                  autoCapitalize="none"
                  autoCompleteType="off"
                  autoCorrect
                  keyboardType="default"
                  label="Bio (optional)"
                  multiline
                  numberOfLines={4}
                  onBlur={handleBlur('bio')}
                  onChangeText={handleChange('bio')}
                  placeholder={artist.biography}
                  value={values.bio}
                />
                <View style={styles.buttonContainer}>
                  <LinearGradientButton
                    disabled={!isValid}
                    title="Save"
                    onPress={handleSubmit}
                  />
                </View>
              </View>
            </>
          )}
        </Formik>
      </View>
    </ScrollView>
  );
}
